import React, { useState, useEffect } from 'react';
import './Steps.css';

const Step5 = ({ bookingData, updateBookingData, nextStep, prevStep }) => {
  const [participants, setParticipants] = useState(bookingData.participants || []);
  const [contactEmail, setContactEmail] = useState(bookingData.contactEmail || '');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [contactPhone, setContactPhone] = useState(bookingData.contactPhone || '');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Initialize participants array if empty
    if (participants.length === 0) {
      const initialParticipants = Array.from({ length: bookingData.numberOfParticipants }, (_, i) => ({
        id: i + 1,
        name: '',
        age: '',
        experienceLevel: 'beginner',
        hasEquipment: false
      }));
      setParticipants(initialParticipants);
    }
  }, [bookingData.numberOfParticipants, participants.length]);

  const validateName = (name) => {
    if (!name || name.trim().length === 0) {
      return 'Numele este obligatoriu';
    }
    
    // Check for numbers
    if (/\d/.test(name)) {
      return 'Numele nu poate conține cifre';
    }
    
    // Check for special characters (allow only letters, spaces, hyphens, and Romanian diacritics)
    if (!/^[a-zA-ZăâîșțĂÂÎȘȚ\s]+$/.test(name)) {
      return 'Numele poate conține doar litere, spații și cratimă';
    }
    
    // Check for at least 2 characters (one word minimum)
    if (name.trim().length < 2) {
      return 'Numele trebuie să aibă cel puțin 2 caractere';
    }
    
    // Check word count (at least 1 word, max 4 words)
    const words = name.trim().split(/\s+/);
    if (words.length > 4) {
      return 'Numele poate avea maximum 4 cuvinte';
    }
    
    return null;
  };

  const validateAge = (age) => {
    if (!age) {
      return 'Vârsta este obligatorie';
    }
    
    const ageNum = parseInt(age);
    
    if (isNaN(ageNum)) {
      return 'Vârsta trebuie să fie un număr';
    }
    
    if (ageNum < 3 || ageNum > 99) {
      return 'Vârsta trebuie să fie între 3 și 99 ani';
    }
    
    return null;
  };

  const validateEmail = (email) => {
    if (!email || email.trim().length === 0) {
      return 'Email-ul este obligatoriu';
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Email invalid';
    }
    
    return null;
  };

  const validateEmailMatch = (email, confirmEmail) => {
    if (!confirmEmail || confirmEmail.trim().length === 0) {
      return 'Confirmarea email-ului este obligatorie';
    }
    
    if (email !== confirmEmail) {
      return 'Email-urile nu se potrivesc';
    }
    
    return null;
  };

  const validatePhone = (phone) => {
    if (!phone || phone.trim().length === 0) {
      return 'Telefonul este obligatoriu';
    }
    
    // Remove spaces for validation
    const cleanPhone = phone.replace(/\s/g, '');
    
    // Romanian phone formats:
    // 07XXXXXXXX
    // +407XXXXXXXX
    // 00407XXXXXXXX
    const phoneRegex = /^(\+?40|0)?7\d{8}$/;
    if (!phoneRegex.test(cleanPhone)) {
      return 'Număr de telefon invalid (format: 07XX XXX XXX sau +407XX XXX XXX)';
    }
    
    return null;
  };
  const handleParticipantChange = (id, field, value) => {
    const updatedParticipants = participants.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    );
    setParticipants(updatedParticipants);
    updateBookingData('participants', updatedParticipants);
  
    // Clear error for this field when user types
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`participant_${id}_${field}`];
      return newErrors;
    });
  };
  
  const handleContactChange = (field, value) => {
    if (field === 'email') {
      setContactEmail(value);
      updateBookingData('contactEmail', value);
    } else if (field === 'confirmEmail') {
      setConfirmEmail(value);
    } else if (field === 'phone') {
      setContactPhone(value);
      updateBookingData('contactPhone', value);
    }
    
    // Clear error when user types
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const handleNext = () => {
    const newErrors = {};
    
    // Validate all participants
    participants.forEach(p => {
      const nameError = validateName(p.name);
      const ageError = validateAge(p.age);
      
      if (nameError) {
        newErrors[`participant_${p.id}_name`] = nameError;
      }
      if (ageError) {
        newErrors[`participant_${p.id}_age`] = ageError;
      }
    });
    
    // Validate contact info
    const emailError = validateEmail(contactEmail);
    const emailMatchError = validateEmailMatch(contactEmail, confirmEmail);
    const phoneError = validatePhone(contactPhone);
    
    if (emailError) {
      newErrors.email = emailError;
    }
    if (emailMatchError) {
      newErrors.confirmEmail = emailMatchError;
    }
    if (phoneError) {
      newErrors.phone = phoneError;
    }
    
    // If there are errors, set them and don't proceed
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // All valid, proceed
    updateBookingData('contactEmail', contactEmail);
    updateBookingData('contactPhone', contactPhone);
    nextStep();
  };

  return (
    <div className="step-container">
      <h2 className="step-heading">Detalii Participanți</h2>
      <p className="step-description">Completați informațiile pentru fiecare participant</p>

      <div className="participants-details">
        {participants.map((participant, index) => (
          <div key={participant.id} className="participant-form">
            <div className="participant-form-header">
              <h3 className="participant-form-title">Participant {index + 1}</h3>
              <label className="equipment-checkbox">
                <input
                  type="checkbox"
                  checked={participant.hasEquipment}
                  onChange={(e) => handleParticipantChange(participant.id, 'hasEquipment', e.target.checked)}
                />
                <span className="equipment-label">
                  ⛷️ Am echipament propriu
                </span>
              </label>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Nume Complet *</label>
                <input
                  type="text"
                  value={participant.name}
                  onChange={(e) => handleParticipantChange(participant.id, 'name', e.target.value)}
                  placeholder="Ex: Ion Popescu"
                  className={`form-input ${errors[`participant_${participant.id}_name`] ? 'error' : ''}`}
                  required
                />
                {errors[`participant_${participant.id}_name`] && (
                  <span className="error-text">{errors[`participant_${participant.id}_name`]}</span>
                )}
              </div>

              <div className="form-group">
                <label>Vârstă *</label>
                <input
                  type="number"
                  value={participant.age}
                  onChange={(e) => handleParticipantChange(participant.id, 'age', e.target.value)}
                  placeholder="Ex: 25"
                  min="3"
                  max="99"
                  className={`form-input ${errors[`participant_${participant.id}_age`] ? 'error' : ''}`}
                  required
                />
                {errors[`participant_${participant.id}_age`] && (
                  <span className="error-text">{errors[`participant_${participant.id}_age`]}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Nivel de Experiență</label>
              <div className="experience-buttons">
                <button
                  type="button"
                  className={`experience-btn ${participant.experienceLevel === 'beginner' ? 'selected' : ''}`}
                  onClick={() => handleParticipantChange(participant.id, 'experienceLevel', 'beginner')}
                >
                  🌱 Începător
                </button>
                <button
                  type="button"
                  className={`experience-btn ${participant.experienceLevel === 'intermediate' ? 'selected' : ''}`}
                  onClick={() => handleParticipantChange(participant.id, 'experienceLevel', 'intermediate')}
                >
                  ⭐ Intermediar
                </button>
                <button
                  type="button"
                  className={`experience-btn ${participant.experienceLevel === 'advanced' ? 'selected' : ''}`}
                  onClick={() => handleParticipantChange(participant.id, 'experienceLevel', 'advanced')}
                >
                  🏆 Avansat
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="contact-section">
        <h3 className="section-title">Informații de Contact</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => handleContactChange('email', e.target.value)}
              placeholder="exemplu@email.com"
              className={`form-input ${errors.email ? 'error' : ''}`}
              required
            />
            {errors.email && (
              <span className="error-text">{errors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label>Confirmă Email *</label>
            <input
              type="email"
              value={confirmEmail}
              onChange={(e) => handleContactChange('confirmEmail', e.target.value)}
              placeholder="exemplu@email.com"
              className={`form-input ${errors.confirmEmail ? 'error' : ''}`}
              required
            />
            {errors.confirmEmail && (
              <span className="error-text">{errors.confirmEmail}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Telefon *</label>
            <input
              type="tel"
              value={contactPhone}
              onChange={(e) => handleContactChange('phone', e.target.value)}
              placeholder="07XX XXX XXX"
              className={`form-input ${errors.phone ? 'error' : ''}`}
              required
            />
            {errors.phone && (
              <span className="error-text">{errors.phone}</span>
            )}
          </div>
        </div>
      </div>

      <div className="step-actions">
        <button className="btn-secondary" onClick={prevStep}>
          ← Înapoi
        </button>
        <button 
          className="btn-primary"
          onClick={handleNext}
        >
          Continuă →
        </button>
      </div>
    </div>
  );
};

export default Step5;